import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import InputForm from "./components/InputForm";
import ProgressSection from "./components/ProgressSection";
import LinkList from "./components/LinkList";
import LinkDetail from "./components/LinkDetail";
import ErrorSection from "./components/ErrorSection";
import AuthErrorBanner from "./components/AuthErrorBanner";
import SyncDestinationPicker from "./components/SyncDestinationPicker";
import SettingsView from "./components/SettingsView";
import KnowledgeView from "./components/KnowledgeView";
import KnowledgeEntityView from "./components/KnowledgeEntityView";
import { getSyncSettings, setSyncPreference, SyncAdapter } from "./utils/api";
import { completeNativeLogin, isOAuthCallbackUrl, noteAppResumed } from "./utils/auth";
import { clearPendingShare, readPendingShare, savePendingShare } from "./utils/pending-share";
import { watchProcessingPicker } from "./utils/processing-picker";
import { App as CapacitorApp } from "@capacitor/app";
import { designReviewLink } from "./mocks/designReviewLink";
import {
  designReviewKnowledgeEntities,
  designReviewKnowledgeEntity,
  designReviewKnowledgeLinks,
  designReviewKnowledgeRelated,
  designReviewKnowledgeStats,
} from "./mocks/designReviewKnowledge";

/** Deep links already processed, so a repeated delivery is ignored. */
const handledCallbacks = new Set<string>();

export const resumeSharedUrl = (
  url: string,
  navigate: (path: string) => void,
  setPendingUrl: (url: string) => void,
): void => {
  savePendingShare(url);
  navigate("/");
  setPendingUrl(url);
};

const App: React.FC = () => {
  const navigate = useNavigate();
  const [pendingUrl, setPendingUrl] = useState<string>("");

  useEffect(() => {
    const handleSharedUrl = (event: any) => {
      const url = event.detail;
      if (url) {
        // Persist before anything else: submitting it may hit a 401 and start a
        // sign-in, which reloads the page and would otherwise lose the share.
        resumeSharedUrl(url, navigate, setPendingUrl);
      }
    };

    // The OAuth callback arrives on the same deep-link channel as shared links,
    // so it has to be claimed first or it gets treated as a URL to process.
    const handleOAuthCallback = async (url: string): Promise<boolean> => {
      if (!isOAuthCallbackUrl(url)) return false;

      // A cold start from a deep link delivers the same URL through both
      // getLaunchUrl() and appUrlOpen. The exchange consumes its PKCE, so the
      // second delivery would fail and replace a successful sign-in with an
      // error.
      if (handledCallbacks.has(url)) return true;
      handledCallbacks.add(url);

      try {
        const resume = await completeNativeLogin(url);
        // Reload rather than navigate: components that already failed their
        // requests while signed out will not refetch on a route change, and the
        // app would sit empty behind a valid token.
        window.location.replace(resume && resume !== "/" ? resume : "/");
      } catch (error) {
        console.error("Sign-in failed:", error);
        navigate("/?auth_error=1");
      }
      return true;
    };

    const handleAppUrlOpen = async (event: any) => {
      if (!event.url) return;
      if (await handleOAuthCallback(event.url)) return;
      resumeSharedUrl(event.url, navigate, setPendingUrl);
    };

    const handleAppStateChange = async () => {
      try {
        const result = await CapacitorApp.getLaunchUrl();
        if (result?.url) {
          if (await handleOAuthCallback(result.url)) return;
          resumeSharedUrl(result.url, navigate, setPendingUrl);
        }
      } catch {
        // No launch URL available
      }
    };

    // Coming back without a callback is how a dismissed sign-in announces
    // itself: the pending PKCE would otherwise keep every later 401 from
    // starting a new one, leaving the app stuck reporting an expired session.
    const handleResume = (state: { isActive: boolean }): void => {
      if (state.isActive) noteAppResumed();
    };

    CapacitorApp.addListener("appUrlOpen", handleAppUrlOpen);
    CapacitorApp.addListener("appStateChange", handleResume);
    window.addEventListener("sharedUrl", handleSharedUrl);

    // A share that triggered a sign-in is waiting here when the app comes back.
    const parked = readPendingShare();
    if (parked) setPendingUrl(parked);

    handleAppStateChange();

    return () => {
      CapacitorApp.removeAllListeners();
      window.removeEventListener("sharedUrl", handleSharedUrl);
    };
  }, [navigate]);

  return (
    <div className="app-container">
      <Header />
      <main className="app-content">
        <AuthErrorBanner />
        <Routes>
          <Route path="/" element={<ListView pendingUrl={pendingUrl} onPendingUrlConsumed={() => setPendingUrl("")} />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="/processing/:id" element={<ProcessingView />} />
          <Route path="/links/:id" element={<DetailView />} />
          <Route path="/knowledge" element={<KnowledgeBrowseView />} />
          <Route path="/knowledge/:id" element={<KnowledgeDetailView />} />
          <Route path="/design-review/link-detail" element={<DesignReviewDetailView />} />
          <Route path="/design-review/knowledge" element={<DesignReviewKnowledgeView />} />
          <Route path="/design-review/knowledge-entity" element={<DesignReviewKnowledgeEntityView />} />
        </Routes>
      </main>
    </div>
  );
};

interface ListViewProps {
  pendingUrl: string;
  onPendingUrlConsumed: () => void;
}

const ListView: React.FC<ListViewProps> = ({ pendingUrl, onPendingUrlConsumed }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [sharedUrl, setSharedUrl] = useState<string>("");
  const [autoSubmit, setAutoSubmit] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [activeAdapter, setActiveAdapter] = useState<SyncAdapter>("outline");

  useEffect(() => {
    void getSyncSettings()
      .then((settings) => setActiveAdapter(settings.primaryAdapter))
      .catch(() => setActiveAdapter("outline"));
  }, [refreshTrigger]);

  useEffect(() => {
    if (pendingUrl) {
      setSharedUrl(pendingUrl);
      setTimeout(() => setAutoSubmit(true), 50);
      onPendingUrlConsumed();
    }
  }, [pendingUrl, onPendingUrlConsumed]);

  const handleSubmitSuccess = (id: string): void => {
    // Cleared on success only. A failed submit keeps the share parked so the
    // sign-in it may have triggered can still resume it; the TTL bounds the
    // rest, so a URL the backend refuses cannot retry forever.
    clearPendingShare();
    navigate(`/processing/${id}`);
  };

  const handleDelete = (): void => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <>
      <InputForm
        onSubmitSuccess={handleSubmitSuccess}
        onExisting={(id) => {
          clearPendingShare();
          navigate(`/links/${id}`);
        }}
        onError={setError}
        initialUrl={sharedUrl}
        onUrlChange={setSharedUrl}
        autoSubmit={autoSubmit}
        onAutoSubmitComplete={() => setAutoSubmit(false)}
      />
      {error && (
        <ErrorSection error={error} onTryAgain={() => setError(null)} />
      )}
      <LinkList
        activeAdapter={activeAdapter}
        onSelectLink={(id) => navigate(`/links/${id}`)}
        onRetry={(id) => navigate(`/processing/${id}`)}
        onDelete={handleDelete}
        refreshTrigger={refreshTrigger}
      />
    </>
  );
};

const ProcessingView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<number>(0);
  const [contentType, setContentType] = useState<string>("Content");
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(true);
  const [activeAdapter, setActiveAdapter] = useState<SyncAdapter>("outline");
  const [syncEnabled, setSyncEnabled] = useState(false);
  const pickerResolved = useRef(false);

  useEffect(() => {
    void getSyncSettings()
      .then((settings) => {
        setActiveAdapter(settings.primaryAdapter);
        setSyncEnabled(Boolean(settings.anyAdapterConnected));
        setShowPicker(Boolean(settings.anyAdapterConnected));
      })
      .catch(() => {
        setActiveAdapter("outline");
        setSyncEnabled(false);
        setShowPicker(false);
      });
  }, []);

  const handleSyncSelected = async (pageId?: string, adapter?: SyncAdapter) => {
    if (!id || pickerResolved.current) return;
    pickerResolved.current = true;
    setShowPicker(false);
    try {
      await setSyncPreference(id, pageId, adapter || activeAdapter);
    } catch (err) {
      console.error('Failed to set sync preference:', err);
    }
  };

  useEffect(() => {
    if (!syncEnabled || !showPicker || !id) return;
    return watchProcessingPicker(window, document, () => void handleSyncSelected());
  }, [syncEnabled, showPicker, id]);

  const handleComplete = (): void => {
    if (syncEnabled && showPicker) void handleSyncSelected();
    navigate(`/links/${id}`, { replace: true });
  };

  const handleError = (errorMessage: string): void => {
    setError(errorMessage);
  };

  if (error) {
    return (
      <ErrorSection error={error} onTryAgain={() => navigate("/", { replace: true })} />
    );
  }

  return (
    <>
      {syncEnabled && showPicker && id && (
        <SyncDestinationPicker
          linkId={id}
          activeAdapter={activeAdapter}
          onSelect={handleSyncSelected}
          onClose={() => void handleSyncSelected()}
        />
      )}
      <ProgressSection
        processId={id || null}
        progress={progress}
        contentType={contentType}
        onComplete={handleComplete}
        onError={handleError}
        onProgressUpdate={(value, type) => {
          setProgress(value);
          if (type) setContentType(type);
        }}
      />
    </>
  );
};

const DetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <LinkDetail
      linkId={id || ""}
      onBack={() => navigate("/")}
    />
  );
};

const KnowledgeBrowseView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <KnowledgeView
      onBack={() => navigate("/")}
      onSelectEntity={(id) => navigate(`/knowledge/${id}`)}
    />
  );
};

const KnowledgeDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <KnowledgeEntityView
      // Remounting on id keeps the screen from showing the previous entity's
      // related list while the next one loads.
      key={id}
      entityId={id || ""}
      onBack={() => navigate("/knowledge")}
      onSelectEntity={(entityId) => navigate(`/knowledge/${entityId}`)}
      onSelectLink={(linkId) => navigate(`/links/${linkId}`)}
    />
  );
};

const DesignReviewKnowledgeView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <KnowledgeView
      previewMode
      initialEntities={designReviewKnowledgeEntities}
      initialStats={designReviewKnowledgeStats}
      onBack={() => navigate("/")}
      onSelectEntity={() => undefined}
    />
  );
};

const DesignReviewKnowledgeEntityView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <KnowledgeEntityView
      previewMode
      entityId={designReviewKnowledgeEntity.id}
      initialData={{
        entity: designReviewKnowledgeEntity,
        related: designReviewKnowledgeRelated,
        links: designReviewKnowledgeLinks,
      }}
      onBack={() => navigate("/knowledge")}
      onSelectEntity={() => undefined}
      onSelectLink={() => undefined}
    />
  );
};

const DesignReviewDetailView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <LinkDetail
      linkId={designReviewLink.id}
      initialData={designReviewLink}
      previewMode
      onBack={() => navigate("/")}
    />
  );
};

export default App;
